import jwt_decode from 'jwt-decode'
import { isEmpty } from 'underscore'
import SessionManager from './SessionManager'
import { GetStorage, GetTokenStorageKey, GetTokenStorageKeyPrefix } from './StorageKeys'

class TokenSessionManager extends SessionManager {

  constructor(settings) {
    console.debug('Setting up TokenSessionManager')

    settings.tokenStorageType = 'local'


    super(settings)
  }

  async setup() {
    const setupRes = await super.setup()
    const userSessionPayload = this.loadUserSession()

    this.settings = {
      ...userSessionPayload,
      ...this.settings,
    }

    return setupRes
  }

  async initialize() {
    console.debug('TokenSessionManager initializing')
    const initRes = await super.initialize()
    const result = initRes ? initRes : {
      init: false,
      hasExecutedRefreshToken: false,
    }
    if (await this.isLogged() && !result.hasExecutedRefreshToken) {
      console.debug('TokenSessionManager saving user session')
      this.saveUserSession()
    } else {
      console.debug('TokenSessionManager Not logged')
    }

    return result.init
  }

  isLogged() {
    console.debug('TokenSessionManager Checking if the user is logged')
    return (this.isKcLogged() || this.isTokenLogged()) && this.hasNecessaryCookiesForLegacyCompatibility()
  }

  isTokenLogged() {
    console.debug('Trying to auth using the stored tokens')
    const tokenData = this.getTokenData()

    if (!tokenData) {
      return false
    } else {
      return (new Date().getTime() / 1000) <= tokenData.exp
    }
  }

  isKcLogged() {
    if (!this.keyCloakInstance) {
      console.debug('TokenSessionManager no keyCloakInstance running')
      return false
    }

    console.debug('TokenSessionManager keyCloakInstance found')

    return this.keyCloakInstance.authenticated
  }

  getStorageKey() {
    return GetTokenStorageKey(this.settings.clientId)
  }

  getStorageKeyPrefix() {
    return GetTokenStorageKeyPrefix()
  }

  saveUserSession() {
    if (this.isKcLogged()) {
      const userSessionPayload = {
        access_token: this.keyCloakInstance.token,
        id_token: this.keyCloakInstance.idToken,
        refresh_token: this.keyCloakInstance.refreshToken,
      }
      GetStorage(this.settings.tokenStorageType || 'local').setItem(
        this.getStorageKey(), JSON.stringify(userSessionPayload)
      )
    }
  }

  getTokenData() {
    const userSession = this.loadUserSession()
    if (!isEmpty(userSession)) {
      const decoded = jwt_decode(userSession['access_token'])
      return decoded
    }

    const accessToken = this.settings.access_token
    if (accessToken) {
      return jwt_decode(accessToken)
    }
  }

  async getUserInfo(accessToken) {

    // eslint-disable-next-line no-undef
    const url = `${this.settings.url}/realms/${this.settings.realm}/protocol/openid-connect/userinfo`
    console.debug('client:', this.settings.clientId, 'Fetching user info from: ', url)

    const headers = new Headers()

    headers.append('Content-type', 'application/json')
    headers.append('Authorization', 'Bearer ' + accessToken)

    const data = {
      method: 'POST',
      headers: headers,
      credentials: 'include',
      timeout: 5000
    }

    return await fetch(url, data)
      .then(res => {
        if (res.status == 401) {
          this.clearUserSession()
        }
        return res.json()
      })
      .catch(() => { throw new Error('[GloboID.js] API error') })
  }

  loadUserSession() {
    const userSessionPayload = GetStorage(this.settings.tokenStorageType || 'local')
      .getItem(this.getStorageKey())

    if (userSessionPayload) {
      return JSON.parse(userSessionPayload)
    }
  }

  clearUserSession() {
    this.removeEntriesStartingWith(this.getStorageKeyPrefix(), localStorage)
    this.removeEntriesStartingWith(this.getStorageKeyPrefix(), sessionStorage)
  }

  async logout() {
    super.logout()
  }

  removeEntriesStartingWith(prefix, storage) {
    Object.keys(storage).forEach(key => {
      if (key.startsWith(prefix)) {
        storage.removeItem(key)
      }
    })
  }

  async loadUserInfo() {
    const tokens = await this.RefreshTokens(true)

    if (!isEmpty(tokens)) {
      const accessToken = tokens['access_token']
      const idToken = tokens['id_token']
      const decodedIdToken = jwt_decode(idToken)

      try {
        const userInfo = await this.getUserInfo(accessToken)
        userInfo.photoUrl = userInfo.picture
        userInfo.userName = userInfo.name

        if (decodedIdToken && decodedIdToken.glbid) {
          userInfo.glbid = decodedIdToken.glbid
        }

        return userInfo

      } catch (error) {
        console.error('Error fetching user info:', error)
      }
    }
    return {}
  }

  getTokens() {
    if (this.isLogged()) {
      const key = GetTokenStorageKey(this.settings.clientId)
      const tokens = GetStorage(this.settings.tokenStorageType || 'local').getItem(key)
      return JSON.parse(tokens)
    } else {
      throw new Error('[TokenSessionManager] user not logged')
    }
  }


  async getUpdatedTokens() {
    try {
      const { access_token, id_token, refresh_token } = await this.RefreshTokens()
      return { access_token, id_token, refresh_token }
    } catch (error) {
      throw new Error('[TokenSessionManager] failed on get tokens: ' + error.message)
    } 
  }
}

export { TokenSessionManager }
